/**
 * Organization experience-profile registry — CLIENT side
 * (CC-V35-BANK-PROFILE-01; accepted design §5.3/§5.6/§5.7/§6; D-098 tranche 2).
 *
 * The code registry is the source of MEANING for an organization's stored
 * (profile_key, profile_version) tuple: landing destination, navigation
 * composition, terminology bundle, primary object. The server carries a
 * key/version mirror (`server/experienceProfiles/registry.ts`); the
 * registryParity suite mechanically proves the two enumerations never drift.
 *
 * PRESERVATION (§6.1): `core_dataset_v1`'s navigation is the SEVEN rail
 * descriptors moved from AppShell.tsx WITHOUT EDIT — keys, labels,
 * destinations, isActive semantics, and SVG geometry are byte-for-byte the
 * pre-change constants. Any diff is a defect, not a trade-off. Icons are
 * expressed via React.createElement (not JSX) so this module is importable
 * from the node-side parity suite; the rendered output is identical.
 *
 * BANKING (§6.2): `banking_use_case_v1` is the accepted profile definition
 * and shell composition ONLY — navigation identities and a landing path. The
 * use-case inventory surface itself is DC-1781 and is NOT implemented or
 * faked here. `administration` deliberately reuses the existing authorized
 * settings surface (D-096: route reuse of an authorized surface is not
 * dataset-profile leakage; identity is what must not leak).
 *
 * ARC IP boundary: identifiers and generic product labels only. No ARC
 * framework content, control text, template content, or workbook material.
 *
 * Fail-closed (§5.10): unknown or version-drifted lookups THROW. There is no
 * default and no fallback profile. localStorage is never an input here —
 * resolution takes the server-supplied tuple and nothing else.
 */

import * as React from 'react';

export interface NavDescriptor {
  key: string;
  label: string;
  /**
   * The destination, or null for a descriptor that names a stage of the
   * product that is not built yet. A rail entry pointing at an unregistered
   * route is a dead end, and a dead end in front of a customer is worse than
   * an absent link — so an unbuilt stage carries no `to` at all and the shell
   * renders it as non-navigable (D-BANK-UX-03).
   */
  to: string | null;
  /** Returns true when this destination owns the current location. */
  isActive: (pathname: string, search: string) => boolean;
  icon: React.ReactNode;
  /**
   * 'in_development' names a surface the product intends and has not shipped.
   * Absent means available. The rail keeps these visible because the sequence
   * they sit in IS the explanation of the product — hiding them would make the
   * shipped surfaces look like the whole of it.
   */
  status?: 'available' | 'in_development';
}

export interface ExperienceProfileDefinition {
  key: 'core_dataset' | 'banking_use_case';
  version: number;
  /** Code identity: `${key}_v${version}` — must match the server registry. */
  identity: string;
  landingPath: string;
  navDescriptors: NavDescriptor[];
  /** Names a bundle; never free text from the database. */
  terminologyBundle: string;
  primaryObjectLabel: string;
}

const stroke = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const e = React.createElement;

function svgIcon(...children: React.ReactNode[]): React.ReactNode {
  return e(
    'svg',
    { viewBox: '0 0 24 24', ...stroke, 'aria-hidden': true },
    ...children
  );
}

// ============================================================================
// core_dataset_v1 navigation — AppShell.tsx DESTS, moved without edit.
// ============================================================================

const CORE_DATASET_NAV: NavDescriptor[] = [
  {
    key: 'home',
    label: 'Home',
    to: '/home',
    isActive: (p) => p === '/home' || p === '/',
    icon: svgIcon(
      e('path', { key: 'a', d: 'M3 10.5 12 3l9 7.5' }),
      e('path', { key: 'b', d: 'M5 9.5V21h14V9.5' })
    ),
  },
  {
    key: 'journeys',
    label: 'Journeys',
    to: '/journeys',
    isActive: (p) =>
      p.startsWith('/journeys') || p.startsWith('/journey/') || p.startsWith('/analysis/') || p.startsWith('/import/'),
    icon: svgIcon(
      e('circle', { key: 'a', cx: 6, cy: 19, r: 2 }),
      e('circle', { key: 'b', cx: 18, cy: 5, r: 2 }),
      e('path', { key: 'c', d: 'M8 19h6a4 4 0 0 0 0-8H10a4 4 0 0 1 0-8h6' })
    ),
  },
  {
    key: 'readiness',
    label: 'Readiness',
    to: '/readiness',
    isActive: (p) => p.startsWith('/readiness') || p.startsWith('/triangulation'),
    icon: svgIcon(
      e('path', { key: 'a', d: 'M12 21a9 9 0 1 1 9-9' }),
      e('path', { key: 'b', d: 'm12 12 5-3' }),
      e('path', { key: 'c', d: 'M19.5 17.5 21 21h-4' })
    ),
  },
  {
    key: 'ontology',
    label: 'Ontology',
    to: '/ontology',
    isActive: (p) => p.startsWith('/settings/ontologies') || p.startsWith('/ontology'),
    icon: svgIcon(
      e('circle', { key: 'a', cx: 5, cy: 6, r: 2.2 }),
      e('circle', { key: 'b', cx: 19, cy: 6, r: 2.2 }),
      e('circle', { key: 'c', cx: 12, cy: 18, r: 2.2 }),
      e('path', { key: 'd', d: 'M6.5 7.8 10.7 16M17.5 7.8 13.3 16M7.2 6h9.6' })
    ),
  },
  {
    key: 'people',
    label: 'People',
    to: '/settings/team',
    isActive: (p) => p.startsWith('/settings/team'),
    icon: svgIcon(
      e('circle', { key: 'a', cx: 9, cy: 8, r: 3.2 }),
      e('path', { key: 'b', d: 'M3.5 20c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5' }),
      e('path', { key: 'c', d: 'M16 5.5a3 3 0 0 1 0 5.4M17.8 15.2c1.6.6 2.7 2 3.2 4' })
    ),
  },
  {
    key: 'governance',
    label: 'Governance',
    to: '/settings/governance',
    isActive: (p) => p.startsWith('/settings/governance'),
    icon: svgIcon(
      e('path', { key: 'a', d: 'M12 3 4.5 6v5c0 4.6 3 8.4 7.5 10 4.5-1.6 7.5-5.4 7.5-10V6Z' }),
      e('path', { key: 'b', d: 'm9 11.5 2.2 2.2L15.5 9.5' })
    ),
  },
  {
    key: 'settings',
    label: 'Settings',
    to: '/settings',
    isActive: (p) =>
      p.startsWith('/settings') &&
      !p.startsWith('/settings/governance') &&
      !p.startsWith('/settings/team') &&
      !p.startsWith('/settings/ontologies'),
    icon: svgIcon(
      e('circle', { key: 'a', cx: 12, cy: 12, r: 3.2 }),
      e('path', {
        key: 'b',
        d: 'M12 2.8v2.7M12 18.5v2.7M2.8 12h2.7M18.5 12h2.7M5.5 5.5l1.9 1.9M16.6 16.6l1.9 1.9M18.5 5.5l-1.9 1.9M7.4 16.6l-1.9 1.9',
      })
    ),
  },
];

// ============================================================================
// banking_use_case_v1 navigation — the §6.2 identities. Generic product
// iconography; no ARC content.
// ============================================================================

const BANKING_NAV: NavDescriptor[] = [
  {
    // D-BANK-UX-03: the front door. The inventory was the landing surface and
    // made a poor one — an operational workspace cannot also be the thing that
    // explains the product to someone seeing it for the first time.
    key: 'overview',
    label: 'Overview',
    to: '/overview',
    isActive: (p) => p === '/' || p === '/overview',
    icon: svgIcon(
      e('path', { key: 'a', d: 'M3.5 11.5 12 4l8.5 7.5' }),
      e('path', { key: 'b', d: 'M6 10.5V20h12v-9.5' }),
      e('path', { key: 'c', d: 'M10 20v-5.5h4V20' })
    ),
  },
  {
    key: 'use-cases',
    label: 'AI Use Cases',
    to: '/use-cases',
    isActive: (p) => p.startsWith('/use-cases'),
    icon: svgIcon(
      e('rect', { key: 'a', x: 3.5, y: 3.5, width: 7.5, height: 7.5, rx: 1.5 }),
      e('rect', { key: 'b', x: 13, y: 3.5, width: 7.5, height: 7.5, rx: 1.5 }),
      e('rect', { key: 'c', x: 3.5, y: 13, width: 7.5, height: 7.5, rx: 1.5 }),
      e('rect', { key: 'd', x: 13, y: 13, width: 7.5, height: 7.5, rx: 1.5 })
    ),
  },
  {
    key: 'arc-library',
    label: 'ARC Library',
    to: '/arc-library',
    isActive: (p) => p.startsWith('/arc-library'),
    icon: svgIcon(
      e('path', { key: 'a', d: 'M4 4.5h5.5v15H4z' }),
      e('path', { key: 'b', d: 'M9.5 4.5H15v15H9.5z' }),
      e('path', { key: 'c', d: 'm15.5 5.5 4.5 1-3.2 13-4.5-1z' })
    ),
  },
  {
    // CC-V35-BANK-DEMO-READY-01. The banking-ONLY custom metrics — population
    // stability and the adverse-impact (four-fifths) screen — execute inside
    // the data-quality engine and, per the metric registry, run for NO other
    // profile. The original six-destination rail nevertheless gave a Banking
    // organization no way to reach that engine: access was never gated (no
    // core route carries a profile gate), but there was no click-path, so the
    // metrics were navigationally unreachable by the only profile that runs
    // them.
    //
    // Reuse of an already-authorized surface, exactly as `administration`
    // reuses /settings (D-096: reuse is not leakage; identity is). Banking
    // keeps its own key and label — the dataset rail's `home`/`Home` identity
    // never appears here — and the profile's landing stays /use-cases, so the
    // AI use case remains the primary object.
    key: 'datasets',
    label: 'Datasets',
    to: '/home',
    isActive: (p) =>
      p === '/home' ||
      p.startsWith('/journeys') ||
      p.startsWith('/journey/') ||
      p.startsWith('/analysis/') ||
      p.startsWith('/import'),
    icon: svgIcon(
      e('ellipse', { key: 'a', cx: 12, cy: 6, rx: 7, ry: 2.8 }),
      e('path', { key: 'b', d: 'M5 6v6c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8V6' }),
      e('path', { key: 'c', d: 'M5 12v6c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8v-6' })
    ),
  },
  {
    // The three stages below are named by the accepted design and NOT built:
    // no /assessments, /evidence, or /portfolio-risk route has ever been
    // registered for this profile, so until D-BANK-UX-03 these rendered as
    // links into a 404. They stay in the rail — the sequence is how the
    // product explains itself — but they are not navigable and they say so.
    key: 'assessments',
    label: 'Assessments',
    to: null,
    status: 'in_development',
    isActive: () => false,
    icon: svgIcon(
      e('path', { key: 'a', d: 'M6 3.5h12v17H6z' }),
      e('path', { key: 'b', d: 'm8.5 12 2 2 4-4.5' }),
      e('path', { key: 'c', d: 'M9 7.5h6' })
    ),
  },
  {
    key: 'evidence',
    label: 'Evidence',
    to: null,
    status: 'in_development',
    isActive: () => false,
    icon: svgIcon(
      e('path', { key: 'a', d: 'M4 6.5 8 4l4 2.5V19l-4-2.5L4 19z' }),
      e('path', { key: 'b', d: 'M12 6.5 16 4l4 2.5V19l-4-2.5-4 2.5' })
    ),
  },
  {
    key: 'portfolio-risk',
    label: 'Portfolio',
    to: null,
    status: 'in_development',
    isActive: () => false,
    icon: svgIcon(
      e('path', { key: 'a', d: 'M4 20V10M10 20V4M16 20v-8M21 20H3' }),
      e('path', { key: 'b', d: 'm16 8 3-3' })
    ),
  },
  {
    // Deliberate reuse of the existing authorized settings surface — a
    // Banking administrator manages the same org administration screens.
    key: 'administration',
    label: 'Administration',
    to: '/settings',
    isActive: (p) => p.startsWith('/settings'),
    icon: svgIcon(
      e('circle', { key: 'a', cx: 12, cy: 12, r: 3.2 }),
      e('path', {
        key: 'b',
        d: 'M12 2.8v2.7M12 18.5v2.7M2.8 12h2.7M18.5 12h2.7M5.5 5.5l1.9 1.9M16.6 16.6l1.9 1.9M18.5 5.5l-1.9 1.9M7.4 16.6l-1.9 1.9',
      })
    ),
  },
];

// ============================================================================
// The registry
// ============================================================================

export const EXPERIENCE_PROFILES: ReadonlyArray<ExperienceProfileDefinition> = [
  {
    key: 'core_dataset',
    version: 1,
    identity: 'core_dataset_v1',
    landingPath: '/home',
    navDescriptors: CORE_DATASET_NAV,
    terminologyBundle: 'core_dataset',
    primaryObjectLabel: 'Dataset',
  },
  {
    key: 'banking_use_case',
    version: 1,
    identity: 'banking_use_case_v1',
    landingPath: '/overview',
    navDescriptors: BANKING_NAV,
    terminologyBundle: 'banking_use_case',
    primaryObjectLabel: 'AI use case',
  },
];

export class ClientExperienceProfileError extends Error {
  constructor(key: string, version: number) {
    super(
      `Unknown experience profile ('${key}', ${version}) — the client registry ` +
        `defines no such profile and will not substitute another. This is ` +
        `code/data drift; see the §5.10 degraded-shell behavior.`
    );
    this.name = 'ClientExperienceProfileError';
  }
}

export function resolveProfileDefinition(
  key: string,
  version: number
): ExperienceProfileDefinition {
  const def = EXPERIENCE_PROFILES.find(
    (p) => p.key === key && p.version === version
  );
  if (!def) {
    throw new ClientExperienceProfileError(key, version);
  }
  return def;
}

export function getNavDescriptors(key: string, version: number): NavDescriptor[] {
  return resolveProfileDefinition(key, version).navDescriptors;
}
